import React, { useState, useMemo } from 'react';
import { FloatingInput } from '../../ui/FloatingInput';
import { Select } from '../../ui/Select';
import { Button } from '../../ui/Button';
import { Card } from '../../ui/Card';
import { ChevronRight, ShieldCheck, Zap } from '../../ui/Icons';
import { cn } from '../../../utils/cn';
import { repConfig } from '../../../config';

const VEHICLE_DATA = {
  Toyota: ['Camry', 'Corolla', 'RAV4', 'Tacoma', 'Tundra', 'Highlander', '4Runner', 'Sienna', 'Prius'],
  Ford: ['F-150', 'Explorer', 'Mustang', 'Escape', 'Ranger', 'Bronco', 'Edge', 'Expedition', 'F-250'],
  Chevrolet: ['Silverado', 'Equinox', 'Tahoe', 'Suburban', 'Malibu', 'Colorado', 'Traverse', 'Corvette'],
  Honda: ['Civic', 'Accord', 'CR-V', 'Pilot', 'Odyssey', 'Ridgeline', 'HR-V', 'Passport'],
  Tesla: ['Model 3', 'Model Y', 'Model S', 'Model X', 'Cybertruck'],
  BMW: ['3 Series', '5 Series', 'X3', 'X5', 'X7', '7 Series', 'M3', 'M5'],
  'Mercedes-Benz': ['C-Class', 'E-Class', 'S-Class', 'GLC', 'GLE', 'GLS', 'G-Wagon'],
  Jeep: ['Wrangler', 'Grand Cherokee', 'Gladiator', 'Cherokee', 'Renegade', 'Compass'],
  Nissan: ['Altima', 'Sentra', 'Rogue', 'Pathfinder', 'Frontier', 'Titan', 'Murano'],
  Hyundai: ['Elantra', 'Sonata', 'Tucson', 'Santa Fe', 'Palisade', 'Kona', 'Ioniq 5']
};

const YEARS = Array.from({ length: 16 }, (_, i) => (2025 - i).toString());
const MAKES = Object.keys(VEHICLE_DATA).sort();

export function StepTradeDetails({ data, updateData, onNext }) {
  const [isAnalyzed, setIsAnalyzed] = useState(false);
  const conditions = ['Excellent', 'Good', 'Fair', 'Poor'];

  const availableModels = useMemo(() => {
    if (!data.tradeMake) return [];
    return VEHICLE_DATA[data.tradeMake] || [];
  }, [data.tradeMake]);

  const handleMakeChange = (make) => {
    updateData({ tradeMake: make, tradeModel: '' });
  };

  const handleConditionSelect = (cond) => {
    updateData({ tradeCondition: cond });
    if (data.tradeYear && data.tradeMake && data.tradeModel && data.tradeMileage) {
      setTimeout(() => setIsAnalyzed(true), 300);
    }
  };

  if (isAnalyzed) {
    return (
      <div className="w-full space-y-4 md:space-y-10 text-center flex flex-col h-full justify-center">
        <div className="space-y-2">
          <div className="w-12 h-12 md:w-20 md:h-20 bg-brand-blue/20 rounded-full flex items-center justify-center text-brand-blue mx-auto mb-2 md:mb-6">
            <ShieldCheck size={24} className="md:w-10 md:h-10" />
          </div>
          <h2 className="font-heading text-xl md:text-5xl font-bold">Analysis Complete</h2>
          <p className="text-gray-400 text-sm md:text-lg max-w-lg mx-auto">
            Matches found for your <span className="text-white font-semibold">{data.tradeYear} {data.tradeMake}</span>
          </p>
        </div>

        <Card className="p-4 md:py-10 border-brand-blue/30 bg-gradient-to-b from-brand-blue/10 to-transparent text-left">
          <div className="flex items-start gap-3 md:gap-4">
            <div className="mt-1 text-brand-blue"><Zap size={18} className="md:w-6 md:h-6" /></div>
            <div>
              <h4 className="font-bold text-sm md:text-xl">Review Pending</h4>
              <p className="text-gray-400 text-xs md:text-base leading-relaxed">
                {repConfig.name} will provide your custom valuation.
              </p>
            </div>
          </div>
        </Card>

        <div className="pt-2 md:pt-4">
          <Button onClick={onNext} className="h-12 md:h-16 text-sm md:text-lg">
            Continue <ChevronRight size={16} />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-xl mx-auto space-y-4 md:space-y-8 flex flex-col h-full justify-center py-2">
      <div className="text-center">
        <h2 className="font-heading text-xl md:text-4xl font-bold">Trade Details</h2>
      </div>

      <form className="space-y-3 md:space-y-6 bg-dark-card p-4 md:p-8 rounded-2xl border border-white/5 shadow-xl overflow-y-auto max-h-[70vh]">
        <div className="grid grid-cols-2 gap-3 md:gap-6">
          <Select 
            label="Year" 
            required 
            options={YEARS} 
            value={data.tradeYear} 
            onChange={val => updateData({ tradeYear: val })} 
          />
          <Select 
            label="Make" 
            required 
            options={MAKES} 
            value={data.tradeMake} 
            onChange={handleMakeChange} 
          />
        </div>

        <Select 
          label="Model" 
          required 
          options={availableModels} 
          value={data.tradeModel} 
          onChange={val => updateData({ tradeModel: val })} 
          className={!data.tradeMake ? "opacity-50 pointer-events-none" : ""}
        />

        <FloatingInput 
          label="Mileage" 
          type="number" 
          inputMode="numeric"
          pattern="[0-9]*"
          required 
          value={data.tradeMileage || ''} 
          onChange={e => updateData({ tradeMileage: e.target.value })} 
          className="!mb-2" 
        />

        <div className="space-y-2">
          <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest ml-1">Condition</label>
          <div className="grid grid-cols-2 gap-2">
            {conditions.map(cond => (
              <div 
                key={cond}
                onClick={() => handleConditionSelect(cond)}
                className={cn(
                  "p-2 md:p-3 rounded-lg border text-xs md:text-sm text-center cursor-pointer transition-all",
                  data.tradeCondition === cond 
                    ? "border-brand-blue bg-brand-blue/20 text-white" 
                    : "border-white/5 bg-white-[0.02] text-gray-500"
                )}
              >
                {cond}
              </div>
            ))}
          </div>
        </div>
      </form>
    </div>
  );
}